import jexl from "jexl";
import { DateTime } from 'luxon';

import {
  InternalApplicationStatuses,
  ProgramStatuses,
  EventStatuses,
} from "../common/constants/statuses";
import { get } from "lodash";
import { URL_SEARCH_PARAMS } from "../common/constants/urlParams";
import { SettingNames } from "../programSettings/enums";
import {
  ALLOWED_REDIRECT_HOSTS,
  ALLOWED_REDIRECT_HOST_SUFFIXES,
  DOMAIN_KEY_HEADER_NAME,
} from "../common/constants/domainKeyRedirect";

export const USER_APPLICATION_SESSION_KEY = "userApplicationData";
export const DOMAIN_KEY = "domainKey";

// Query parameter used to carry the originating (source) portal origin across the
// cross-domain redirect, so the target program sub-domain knows which API to request
// a session-bridge transfer token from.
export const BRIDGE_SOURCE_PARAM = "bridgeSource";

// Query parameter used to carry a program sub-domain's origin to the public portal when a
// cross-domain sign-in is initiated. After authentication completes on the public portal,
// the bootstrap redirect uses this value to return the user to that sub-domain.
export const BRIDGE_RETURN_PARAM = "bridgeReturn";

export const envType = process.env.REACT_APP_TYPE ?? "beta";

export const VERIFICATION_ATTEMPT_KEY = "verificationAttempted";
export const VERIFICATION_STATUS_KEY = "lastVerificationStatus";
export const VERIFICATION_ATTEMPTED_VALUE = "true";
export const PROXY_SESSION_ID_KEY = "X-Proxy-Session-Id";

const isLocalRedirectHost = (host) => {
  if (!host) {
    return false;
  }

  return ALLOWED_REDIRECT_HOSTS.includes(host.toLowerCase());
};

// Retrieves a nested value from an object using a dot-separated path.
// Example: getFieldMappingValue({a: {b: 1}}, "a.b") returns 1.
export const getFieldMappingValue = (obj, path) => {
  if (!path || !obj) return undefined;

  // Split the path into keys, reverse and traverse the object
  return path
    .split(".")
    .reverse()
    .reduce(
      (acc, key) => (acc && typeof acc === "object" ? acc[key] : undefined),
      obj,
    );
};

const extractDomainKey = () => {
  // Check URL params for domainkey
  const urlParams = new URLSearchParams(window.location.search);
  let urlDomainKey = null;

  for (const [key, value] of urlParams.entries()) {
    if (key.toLowerCase() === URL_SEARCH_PARAMS.DomainKey) {
      urlDomainKey = value?.toLowerCase();
      break;
    }
  }

  if (urlDomainKey) {
    // Preserve query `domainkey` so bootstrap redirect logic can run before app render.
    sessionStorage.setItem(DOMAIN_KEY, urlDomainKey);
    return urlDomainKey;
  }

  return (
    sessionStorage.getItem(DOMAIN_KEY) ||
    process.env.REACT_APP_DOMAIN_KEY ||
    "evacuation"
  );
};

const getDomainKeyFromUrl = () => {
  const urlParams = new URLSearchParams(window.location.search);
  for (const [key, value] of urlParams.entries()) {
    if (key.toLowerCase() === URL_SEARCH_PARAMS.DomainKey) {
      return value?.toLowerCase() || null;
    }
  }

  return null;
};

const normalizeConfiguredUrl = (configuredUrl) => {
  if (!configuredUrl) {
    return null;
  }

  const trimmedUrl = configuredUrl.trim();
  if (!trimmedUrl) {
    return null;
  }

  const normalizedWithScheme = /^https?:\/\//i.test(trimmedUrl)
    ? trimmedUrl
    : `https://${trimmedUrl}`;

  let parsedUrl;
  try {
    parsedUrl = new URL(normalizedWithScheme);
  } catch {
    return null;
  }

  if (parsedUrl.protocol !== "http:" && parsedUrl.protocol !== "https:") {
    return null;
  }

  if (parsedUrl.protocol === "http:" && !isLocalRedirectHost(parsedUrl.hostname)) {
    return null;
  }

  return parsedUrl.toString();
};

const combinePaths = (basePath, requestPath) => {
  const normalizedBasePath = !basePath || basePath === "/"
    ? ""
    : basePath.replace(/\/+$/, "");

  const normalizedRequestPath = !requestPath
    ? "/"
    : requestPath.startsWith("/") ? requestPath : `/${requestPath}`;

  if (!normalizedBasePath) {
    return normalizedRequestPath;
  }

  if (normalizedRequestPath === "/") {
    return normalizedBasePath;
  }

  return `${normalizedBasePath}${normalizedRequestPath}`;
};

const getQueryWithoutDomainKey = () => {
  const currentParams = new URLSearchParams(window.location.search);
  const filteredParams = new URLSearchParams();

  for (const [key, value] of currentParams.entries()) {
    if (key.toLowerCase() !== URL_SEARCH_PARAMS.DomainKey) {
      filteredParams.append(key, value);
    }
  }

  return filteredParams;
};

const getQueryWithDomainKey = () => new URLSearchParams(window.location.search);

export const cleanupDomainKeyFromUrl = () => {
  if (typeof window === "undefined") {
    return false;
  }

  const hasDomainKey = Array.from(new URLSearchParams(window.location.search).keys())
    .some((key) => key.toLowerCase() === URL_SEARCH_PARAMS.DomainKey);

  if (!hasDomainKey) {
    return false;
  }

  const filteredQuery = getQueryWithoutDomainKey();
  const cleanedUrl = `${window.location.pathname}${filteredQuery.toString() ? `?${filteredQuery.toString()}` : ""}${window.location.hash}`;
  window.history.replaceState(null, "", cleanedUrl);
  return true;
};

const parsePublicPortalUrl = (configuration) => {
  if (!configuration || typeof configuration !== "string") {
    return null;
  }

  try {
    const parsedConfiguration = JSON.parse(configuration);
    if (typeof parsedConfiguration === "string") {
      return parsedConfiguration;
    }

    if (parsedConfiguration && typeof parsedConfiguration.PublicPortalUrl === "string") {
      return parsedConfiguration.PublicPortalUrl;
    }
  } catch {
    return configuration;
  }

  return null;
};

const isAllowedRedirectHost = (host) => {
  if (!host) {
    return false;
  }

  const normalizedHost = host.toLowerCase();
  if (isLocalRedirectHost(normalizedHost)) {
    return true;
  }

  return ALLOWED_REDIRECT_HOST_SUFFIXES.some((suffix) => normalizedHost.endsWith(suffix));
};

// A redirect target is only trusted when the host is allow-listed AND the transport is safe.
// http: is permitted solely for local dev hosts (mirroring normalizeConfiguredUrl); any other
// host must use https so the cross-domain redirect can't be downgraded / MITM'd.
const isAllowedRedirectUrl = (url) => {
  if (url.protocol !== "https:" && url.protocol !== "http:") {
    return false;
  }

  if (url.protocol === "http:" && !isLocalRedirectHost(url.hostname)) {
    return false;
  }

  return isAllowedRedirectHost(url.hostname);
};

const getBridgeReturnFromUrl = () => {
  const urlParams = new URLSearchParams(window.location.search);
  for (const [key, value] of urlParams.entries()) {
    if (key.toLowerCase() === BRIDGE_RETURN_PARAM.toLowerCase()) {
      return value || null;
    }
  }

  return null;
};

// Runs on the public portal after authentication when a cross-domain sign-in was
// initiated from a program sub-domain (carried as `bridgeReturn`). Sends the user back to
// that sub-domain, tagging the current origin as `bridgeSource` so the sub-domain can
// silently establish a session via the session-bridge flow. Returns true when redirecting.
const redirectToBridgeReturn = (bridgeReturn) => {
  let targetUrl;
  try {
    targetUrl = new URL(bridgeReturn);
  } catch {
    return false;
  }

  if (
    !isAllowedRedirectUrl(targetUrl) ||
    targetUrl.origin === window.location.origin
  ) {
    return false;
  }

  targetUrl.searchParams.set(BRIDGE_SOURCE_PARAM, window.location.origin);
  window.location.replace(targetUrl.toString());
  return true;
};

// Origin of the generic/public portal (payments), derived from the configured API endpoint.
// A direct visit to this origin keeps the environment-default program; other origins are
// resolved against the backend.
const getConfiguredPublicPortalOrigin = () => {
  const endpoint = process.env.REACT_APP_API_ENDPOINT;
  if (!endpoint || !endpoint.trim()) {
    return "";
  }

  const trimmedEndpoint = endpoint.trim();

  // A relative endpoint (e.g. "/api") means the API is served same-origin, so the current
  // origin is itself the public portal; there is no separate cross-domain portal to resolve
  // against. Returning the current origin makes resolveProgramFromUrl keep the env default.
  if (trimmedEndpoint.startsWith("/")) {
    return window.location.origin;
  }

  // Accept scheme-less values (e.g. "api.example.com") by defaulting to https, matching how
  // configured portal URLs are normalized elsewhere, instead of throwing and returning "".
  const normalizedEndpoint = normalizeConfiguredUrl(trimmedEndpoint);
  if (!normalizedEndpoint) {
    return "";
  }

  try {
    return new URL(normalizedEndpoint).origin;
  } catch {
    return "";
  }
};

// Runs on a direct visit to a program sub-domain that has no domain key in the URL. Asks the
// backend which program is associated with the current origin, stores its domain key, and
// reloads so the app loads that program's content instead of the environment default. The
// public portal origin (and any origin that maps to no program) keeps the env default.
const resolveProgramFromUrl = async () => {
  // Already resolved earlier this session.
  if (sessionStorage.getItem(DOMAIN_KEY)) {
    return false;
  }

  // The generic public portal keeps the environment-default program.
  const publicPortalOrigin = getConfiguredPublicPortalOrigin();
  if (publicPortalOrigin && publicPortalOrigin.toLowerCase() === window.location.origin.toLowerCase()) {
    return false;
  }

  try {
    const params = new URLSearchParams({ url: window.location.origin });
    const response = await fetch(`/api/program/resolveByUrl?${params.toString()}`, {
      method: "GET",
    });

    if (!response.ok) {
      return false;
    }

    const data = await response.json();
    const resolvedDomainKey = data?.domainKey ? String(data.domainKey).toLowerCase() : null;
    if (!resolvedDomainKey) {
      return false;
    }

    // Persist so the reloaded page (and the domain-key API header) pick up the resolved
    // program, then reload to re-capture the domain key snapshot.
    sessionStorage.setItem(DOMAIN_KEY, resolvedDomainKey);
    window.location.reload();
    return true;
  } catch {
    return false;
  }
};

export const bootstrapDomainKeyRedirect = async () => {
  if (typeof window === "undefined") {
    return false;
  }

  // The bridge-return hop only makes sense on the generic public portal (where sign-in
  // completes). Only honor it when we're on that origin — when it's determinable — and only
  // return early if a redirect actually happened; otherwise fall through to the normal
  // domain-key redirect / program-resolution flow instead of silently skipping it.
  const bridgeReturn = getBridgeReturnFromUrl();
  if (bridgeReturn) {
    const publicPortalOrigin = getConfiguredPublicPortalOrigin();
    const onPublicPortal =
      !publicPortalOrigin ||
      publicPortalOrigin.toLowerCase() === window.location.origin.toLowerCase();

    if (onPublicPortal && redirectToBridgeReturn(bridgeReturn)) {
      return true;
    }
  }

  const urlDomainKey = getDomainKeyFromUrl();
  if (!urlDomainKey) {
    return await resolveProgramFromUrl();
  }

  try {
    const headers = {
      [DOMAIN_KEY_HEADER_NAME]: urlDomainKey,
    };

    const programResponse = await fetch("/api/program", {
      method: "GET",
      headers,
      credentials: "include",
    });

    if (!programResponse.ok) {
      return false;
    }

    const program = await programResponse.json();
    if (!program?.id) {
      return false;
    }

    const settingsParams = new URLSearchParams({
      settingName: SettingNames.PublicPortalUrl,
      programId: program.id,
    });

    const programSettingResponse = await fetch(`/api/programSettings?${settingsParams.toString()}`, {
      method: "GET",
      headers,
      credentials: "include",
    });

    if (!programSettingResponse.ok) {
      return false;
    }

    const programSetting = await programSettingResponse.json();
    const configuredPortalUrl = parsePublicPortalUrl(programSetting?.configuration);
    const normalizedPortalUrl = normalizeConfiguredUrl(configuredPortalUrl);

    if (!normalizedPortalUrl) {
      return false;
    }

    const currentUrl = new URL(window.location.href);
    const redirectBaseUrl = new URL(normalizedPortalUrl);
    if (!isAllowedRedirectHost(redirectBaseUrl.hostname)) {
      return false;
    }

    const targetUrl = new URL(redirectBaseUrl.toString());

    targetUrl.pathname = combinePaths(redirectBaseUrl.pathname, currentUrl.pathname);

    const redirectQuery = getQueryWithDomainKey();
    targetUrl.search = redirectQuery.toString() ? `?${redirectQuery.toString()}` : "";

    const isSameTarget =
      currentUrl.protocol.toLowerCase() === targetUrl.protocol.toLowerCase() &&
      currentUrl.host.toLowerCase() === targetUrl.host.toLowerCase() &&
      currentUrl.pathname === targetUrl.pathname &&
      currentUrl.search === targetUrl.search;

    sessionStorage.setItem(DOMAIN_KEY, urlDomainKey);

    if (isSameTarget) {
      return false;
    }

    // We are about to leave the current (source) origin for the program sub-domain.
    // Record the source origin so the target can request a session-bridge transfer
    // token from it and silently establish a session there.
    targetUrl.searchParams.set(BRIDGE_SOURCE_PARAM, currentUrl.origin);

    window.location.replace(targetUrl.toString());
    return true;
  } catch {
    return false;
  }
};

const removeBridgeSourceFromUrl = () => {
  if (typeof window === "undefined") {
    return;
  }

  const params = new URLSearchParams(window.location.search);
  if (!params.has(BRIDGE_SOURCE_PARAM)) {
    return;
  }

  params.delete(BRIDGE_SOURCE_PARAM);
  const newSearch = params.toString();
  const newUrl = `${window.location.pathname}${newSearch ? `?${newSearch}` : ""}${window.location.hash}`;
  window.history.replaceState(null, "", newUrl);
};

// Runs on the target program sub-domain after a cross-domain redirect. When the user
// already holds a session on the source (generic) portal, this silently bridges that
// session onto the current sub-domain so they arrive authenticated:
//   1. Request a short-lived transfer token from the source portal API (cross-origin,
//      using the source session cookie).
//   2. Redeem the token against the local API to establish the sub-domain session.
// Returns true when a session was successfully established, false otherwise.
export const establishBridgedSession = async () => {
  if (typeof window === "undefined") {
    return false;
  }

  const bridgeSource = new URLSearchParams(window.location.search).get(BRIDGE_SOURCE_PARAM);
  const domainKeyValue = getDomainKeyFromUrl();

  if (!bridgeSource) {
    return false;
  }

  // Only trust a source origin that resolves to an allowed host.
  let sourceUrl;
  try {
    sourceUrl = new URL(bridgeSource);
  } catch {
    removeBridgeSourceFromUrl();
    return false;
  }

  if (
    !isAllowedRedirectUrl(sourceUrl) ||
    sourceUrl.origin === window.location.origin
  ) {
    removeBridgeSourceFromUrl();
    return false;
  }

  try {
    // If a session already exists on this sub-domain, there is nothing to bridge.
    const sessionResponse = await fetch("/api/Auth/session", { credentials: "include" });
    if (sessionResponse.ok) {
      const session = await sessionResponse.json();
      if (session?.isLoggedIn) {
        removeBridgeSourceFromUrl();
        return false;
      }
    }

    // 1. Ask the source portal for a transfer token (uses the source session cookie).
    // When a domain key is present in the URL we pass it through; otherwise the source
    // portal resolves the target program from the request origin.
    const tokenParams = new URLSearchParams();
    if (domainKeyValue) {
      tokenParams.set("domainKey", domainKeyValue);
    }
    const tokenQuery = tokenParams.toString();
    const tokenResponse = await fetch(
      `${sourceUrl.origin}/api/Auth/sessionBridge${tokenQuery ? `?${tokenQuery}` : ""}`,
      { method: "GET", credentials: "include" },
    );

    if (!tokenResponse.ok) {
      removeBridgeSourceFromUrl();
      return false;
    }

    const { transferToken } = await tokenResponse.json();
    if (!transferToken) {
      removeBridgeSourceFromUrl();
      return false;
    }

    // 2. Redeem the token against the local API to establish the sub-domain session.
    const exchangeResponse = await fetch("/api/Auth/sessionBridge/exchange", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token: transferToken }),
    });

    removeBridgeSourceFromUrl();
    return exchangeResponse.ok;
  } catch {
    removeBridgeSourceFromUrl();
    return false;
  }
};

// Used to act like a getter, so that the domain key is up to date whenever it is accessed.
const domainKeyExport = {};
Object.defineProperty(domainKeyExport, "domainKey", {
  get: () => extractDomainKey(),
  enumerable: true,
});

export const { domainKey } = domainKeyExport;

export const refCategories = {
  EVENTS: "events",
  RELATIONSHIP: "relationship",
  GENDER: "gender",
  MONTHS: "months",
  DOCUMENT_TYPES: "documentTypes",
  APP_STATUS: "appStatus",
};

export const Relationship = {
  CHILD: "child",
  SPOUSE: "spouse",
  ADULT: "adult",
  ELDERLY: "elderly",
};

export const getBadgeType = (internalStatus) => {
  let badgeType = "information";
  if (internalStatus === InternalApplicationStatuses.Draft) {
    badgeType = "midtone";
  }
  if (internalStatus === InternalApplicationStatuses.FollowUp) {
    badgeType = "important";
  }
  if (
    internalStatus === InternalApplicationStatuses.Withdrawn ||
    internalStatus === InternalApplicationStatuses.Rejected
  ) {
    badgeType = "dark";
  }
  if (
    internalStatus === InternalApplicationStatuses.PaymentSent ||
    internalStatus === InternalApplicationStatuses.PaymentComplete
  ) {
    badgeType = "success";
  }

  return badgeType;
};

export const formatDate = (date) => {
  return date.toLocaleString("en-US", {
    timeZone: "America/Edmonton", // Adjusts for DST
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

export const EventsEnabled = (programFeatureMapping) => {
  if (!programFeatureMapping) {
    return undefined; // Return undefined when not loaded
  }

  return programFeatureMapping?.isEnabled ?? false;
};

export const isEventTypeActive = (programFeatureEventMapping, event, user) => {
  if (!programFeatureEventMapping && !event) {
    return undefined;
  }

  return (
    event.eventStatus !== EventStatuses.Draft &&
    (user?.isProxyUser || event.eventStatus === EventStatuses.Active)
  );
};

export const viewOnlyApplication = (internalStatus, user) => {
  return (
    !!internalStatus &&
    internalStatus !== InternalApplicationStatuses.Draft &&
    internalStatus !== InternalApplicationStatuses.Submitted &&
    internalStatus !== InternalApplicationStatuses.FollowUp &&
    (!user?.isProxyUser ||
      (internalStatus !== InternalApplicationStatuses.InProgress &&
       internalStatus !== InternalApplicationStatuses.PaymentComplete))
  );
};

// Remove hard coding for finish later and next steps.
// See MPP-4269
export const FINISH_LATER_FORM_ID = "finishLaterConfirmation";
export const NEXT_STEPS_FORM_ID = "nextSteps";

export const NoAvailDocumentTypeId = "1";

export const namePattern =
  "^[a-zA-ZÀ-ÖØ-öø-ÿĀ-žœŒŸçÇëËïÏûÛâÂêÊîÎôÔùÙàÀéÉèÈ'’.\\s-]*$";
export const emailPattern =
  "^$|[a-zA-Z0-9À-ÖØ-öø-ÿ._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$";
export const phonePattern = "^\\(\\d{3}\\)\\s*\\d{3}-?\\d{4}$|^\\d{10}$";

export const nameRegExp = new RegExp(namePattern);
export const phoneRegExp = new RegExp(phonePattern);
export const phoneExtRegExp = /^\d+$/;
export const postalCodeRegExp = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;
export const streetNameRegExp = /^[a-zA-Z0-9\s#,'.\-\/ 'àâäçéèêëîïôöùûüÿœæÉ]*$/;
export const cityNameRegExp = /^[a-zA-Z0-9àâäçéèêëîïôöùûüÿœæÉ\s#,'.\-\/]*$/;
export const bandRegExp = /^\d{3}$/;
export const familyRegExp = /^\d{5}$/;
export const positionRegExp = /^\d{2}$/;
export const fnibRegExp = /^\d{8}$/;
export const multipleSpacesRegExp = /\s+/g;
export const poBoxRegExp = /\bp\.?\s*[o0]\.?\s*box(?:\b|\s*\d+\b)/i;

// Helper function to normalize name fields (trim, replace multiple spaces, lowercase)
export const normalizeName = (name) => {
  return (name || '').toLowerCase().trim().replace(multipleSpacesRegExp, ' ');
};

export const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return "";
  
  // Strip ALL non-digits (This permanently deletes '+' and any letters)
  let digitsOnly = phoneNumber.replace(/\D/g, "");

  // If the user pasted an 11-digit number starting with the '1' country code, strip the 1
  if (digitsOnly.length === 11 && digitsOnly.startsWith("1")) {
    digitsOnly = digitsOnly.substring(1);
  }

  // Force max 10 digits
  digitsOnly = digitsOnly.slice(0, 10);

  // Apply standard North American formatting
  if (digitsOnly.length === 10) {
    return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6, 10)}`;
  }

  return digitsOnly;
};

export const toTitleCase = (str) => {
  if (str) {
    return str.replace(/\w\S*/g, function (txt) {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
  }
};

export const formatPostalCode = (postalCode) => {
  return (postalCode ?? "")
    .replace(/\s/g, "") // Remove whitespace
    .slice(0, 6) // Limit input to 6 characters (e.g., A1A1A1)
    .replace(/([A-Za-z]\d[A-Za-z])(\d[A-Za-z]\d)/, "$1 $2")
    .toUpperCase(); // Format as A1A 1A1
};

export const getGenderFromKey = (key) => {
  const gender = genderList.find((x) => x.key === key);

  return gender?.label || "";
};

export const genderList = [
  { key: 0, label: "Unspecified" },
  { key: 1, label: "Male" },
  { key: 2, label: "Female" },
];

export function getFullName(givenName, middleName, surname) {
  return `${givenName} ${middleName ? middleName + " " : ""}${surname}`;
}

export const provinceList = [
  { label: "Alberta", code: "CA-AB" },
  { label: "British Columbia", code: "CA-BC" },
  { label: "Manitoba", code: "CA-MB" },
  { label: "Newfoundland and Labrador", code: "CA-NL" },
  { label: "Nova Scotia", code: "CA-NS" },
  { label: "New Brunswick", code: "CA-NB" },
  { label: "Ontario", code: "CA-ON" },
  { label: "Prince Edward Island", code: "CA-PE" },
  { label: "Quebec", code: "CA-QC" },
  { label: "Saskatchewan", code: "CA-SK" },
  { label: "Yukon", code: "CA-YT" },
  { label: "Northwest Territories", code: "CA-NT" },
  { label: "Nunavut", code: "CA-NU" },
];

export function getProvinceName(code) {
  const province = provinceList.find((x) => x.code == code);
  return province?.label || " ";
}

export function getFullCountryName(code) {
  if (code === "CA") {
    return "Canada";
  }

  if (code === "US" || code === "USA") {
    return "United States of America";
  }
  return code;
}

export const getAddressDisplay = (
  streetAddressLine1,
  streetAddressLine2,
  locality,
  province,
  postalCode,
  country,
) => {
  return (
    <div>
      <div>{streetAddressLine1}</div>
      {streetAddressLine2 && <div>{streetAddressLine2}</div>}
      <div>{`${locality}, ${getProvinceName(
        province,
      )}${"\u00A0\u00A0"}${postalCode}${
        country ? ` ${getFullCountryName(country)}` : ""
      }`}</div>
    </div>
  );
};

export const getPhoneDisplay = (phone, phoneExtensionLabel, phoneExtension) => {
  if (!phone && !phoneExtension) {
    return "";
  }
  return (
    <>
      {phone}{" "}
      {phoneExtension && (
        <span>
          {phoneExtensionLabel} {phoneExtension}
        </span>
      )}
    </>
  );
};

export const onlyNumberKey = (id) => {
  document.getElementById(id)?.addEventListener("keypress", (event) => {
    if (isNaN(event.key)) {
      event.stopImmediatePropagation();
      event.preventDefault();
    }
  });
};

export const validatePhoneInputField = (e) => {
  // 1. Allow deletion events (Backspace, Delete, Cut) natively, e.data is null for all non insertion events.
  // If there is no text being inserted (e.g., navigation or delete), let it pass
  const incomingText = e.data;
  if (!incomingText) return;

  const isSingleKeystroke = incomingText.length === 1;

  // 2. Strict Payload: ONLY digits (No '+' allowed anymore).
  // Note: We only regex-check single keystrokes. Multi-character pastes bypass 
  // this so the React onChange handler can cleanly sanitize and strip pasted formatting.
  const isIncomingValid = isSingleKeystroke ? /^[0-9]$/.test(incomingText) : true;

  // 3. Pierce the Shadow DOM to get real cursor and selection data
  const path = e.composedPath && e.composedPath();
  const actualInput = path ? path.find(node => node.tagName === 'INPUT') : e.target;
  const currentValue = actualInput?.value || "";
  const hasSelection = actualInput && actualInput.selectionStart !== actualInput.selectionEnd;

  // 4. Count pure digits (ignores formatting like parentheses or hyphens)
  const currentDigitCount = currentValue.replace(/\D/g, "").length;

  // 5. Single-keystroke hard block strictly at 10 digits
  // (paste/autofill and selection replacement natively bypass this)
  const atLengthLimit = !hasSelection && isSingleKeystroke && currentDigitCount >= 10;

  // 6. The Unified Kill Switch
  if (!isIncomingValid || atLengthLimit) {
    e.preventDefault();
    e.stopImmediatePropagation();
  }
};

export const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export const getDateObj = (date) => {
  if (date === null || date === undefined || date === "") {
    return null;
  }

  try {
    // If it's already a Date object, return it
    if (date instanceof Date) {
      return date;
    }

    // Convert from date string to date obj
    if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      const [year, month, day] = date.split("-").map(Number);
      return new Date(year, month - 1, day);
    }

    return new Date(date);
  } catch (e) {
    return null;
  }
};

export const getDate = (date) => {
  const dateObj = getDateObj(date);
  return !dateObj
    ? ""
    : `${dateObj.getDate()} ${months[dateObj.getMonth()]} ${dateObj.getFullYear()}`;
};

export const formatDateForDatabase = (date) => {
  if (date === null || date === undefined || date === "") {
    return null;
  }

  const dateObj = getDateObj(date);
  if (!dateObj) {
    return null;
  }

  // Format as YYYY-MM-DD (ISO date format without time)
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const day = String(dateObj.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

export const formatDateOfBirth = (date) => {
  // Specifically for date of birth - ensures consistent date-only format
  // This is used when saving DOB data to applicationDataJson
  return formatDateForDatabase(date);
};

export const evaluate = (condition, context, form) => {
  if (condition?.expression) {
    try {
      if (condition.context === "user") {
        return jexl.evalSync(condition.expression, context.user);
      }
      if (condition.context === "form") {
        return jexl.evalSync(condition.expression, form);
      }
      if (condition.context) {
        const stepData = context.getStepData(condition.context);
        if (stepData) {
          return jexl.evalSync(condition.expression, stepData);
        }
      }
      if (condition.isEmpty !== undefined) {
        let numOfItems = 0;
        let hasAllDefaultValues = false;
        if (condition.category && context[condition.category]) {
          numOfItems = context[condition.category].length;
        } else if (context[condition.expression]) {
          numOfItems = Object.keys(context[condition.expression]).length;
          if (numOfItems > 0) {
            hasAllDefaultValues = Object.values(
              context[condition.expression],
            ).every((value) =>
              typeof value === "string"
                ? value === ""
                : typeof value === "number"
                  ? value === 0
                  : typeof value === "boolean"
                    ? value === false
                    : value === null || value === undefined,
            );
            if (context[condition.expression]?.isInternal) {
              hasAllDefaultValues = true;
            }
          }
        }
        const result =
          (condition.isEmpty && (!numOfItems || hasAllDefaultValues)) ||
          (!condition.isEmpty && !!numOfItems && !hasAllDefaultValues);
        if (
          (condition.operator === "or" && result) ||
          (condition.operator === "and" && !result) ||
          !condition?.category ||
          condition?.expression === condition?.category
        ) {
          return result;
        }
      }
      if (
        condition.stepData !== "context" &&
        context.stepData &&
        Object.keys(context.stepData)?.length &&
        !condition.stepName
      ) {
        return jexl.evalSync(condition.expression, context.stepData);
      }

      return jexl.evalSync(condition.expression, context);
    } catch (ex) {
      console.error(
        `Exception during evaluating: ${condition?.expression}. Error: ${ex.message}`,
      );
      return false;
    }
  }
  return false;
};

export const tryParse = (json) => {
  try {
    return JSON.parse(json);
  } catch {
    return {};
  }
};

export const calculateAge = (dob) => {
  if (!dob) return null;

  // If dob is a string, convert to JS Date first
  const jsDate = getDateObj(dob);

  const birth = DateTime.fromJSDate(jsDate).startOf('day');

  if (!birth.isValid) return null;
  const age = Math.floor(DateTime.now().startOf('day').diff(birth, 'years').years);
  return age;
};


export const getPaymentMethod = (receivePayment, isProxyUser = "false") => {
  if (receivePayment === "prepaid") {
    if (isProxyUser) {
      return "Prepaid card";
    } else {
      return 'Visit <a target="_blank" href="https://www.alberta.ca/alberta-supports">Alberta Supports Centre</a> in-person for payment';
    }
  }

  return "e-Transfer";
};

export const getETransferMethod = (receivePayment, receiveETransfer) => {
  if (receivePayment === "e-transfer") {
    if (receiveETransfer === "mobile") {
      return "Mobile phone number";
    }
    if (receiveETransfer === "bankAccount") {
      return "Using bank account details";
    }
    return "Email address";
  }

  return "N/A";
};

export const isPortalActive = (portal, user) => {
  return (
    portal?.programStatus !== ProgramStatuses.Draft &&
    portal?.programStatus !== ProgramStatuses.Inactive &&
    (user?.isProxyUser || portal?.isPublicPortalOpen)
  );
};

//Setter
export const setProxySessionId = (id) => {
  if (id) {
    localStorage.setItem(PROXY_SESSION_ID_KEY, id);
  } else {
    localStorage.removeItem(PROXY_SESSION_ID_KEY);
  }
};

// 2. Getter (Simple Read)
export const getProxySessionId = () => {
  return localStorage.getItem(PROXY_SESSION_ID_KEY);
};

// 3. Initializer 
export const initProxySession = () => {
  // ensure window exists
  if (typeof window === 'undefined') return;

  // A. Check URL
  const urlParams = new URLSearchParams(window.location.search);
  const urlSessionId = urlParams.get('proxySessionId');

  if (urlSessionId) {
    
    // B. Save to Storage
    setProxySessionId(urlSessionId);

    // C. Clean URL
    urlParams.delete('proxySessionId');
    const newSearch = urlParams.toString();
    const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '');
    
    // Wrap history update in a small timeout to prevent blocking the render
    setTimeout(() => {
        window.history.replaceState(null, '', newUrl);
    }, 0);
  }
};

